// Copyright (c) 2026 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import * as sdk from '@canton-network/dapp-sdk';
import { useCallback, useEffect, useState } from 'react';

export const useConnect = () => {
  const [status, setStatus] = useState<sdk.dappAPI.StatusEvent>();
  // Initialize as true so we don't flash "Not Connected" states on load
  const [isLoading, setLoading] = useState(true);

  // 1. Stable Connect Action
  const connect = useCallback(async () => {
    try {
      setLoading(true);
      const s = await sdk.connect();
      setStatus(s);
    } catch (err) {
      console.error('Error connecting to wallet:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // 2. Stable Disconnect Action
  const disconnect = useCallback(async () => {
    try {
      await sdk.disconnect();
      setStatus(undefined);
    } finally {
      // Ensure we aren't stuck in loading state if disconnect happens
      setLoading(false);
    }
  }, []);

  // 3. Centralized Synchronization Effect
  useEffect(() => {
    let isActive = true;

    // Defined INSIDE the effect to close over the 'isActive' variable
    const onStatusChanged = (s: sdk.dappAPI.StatusEvent) => {
      if (!isActive) return;
      if (!s.isConnected) {
        setStatus(undefined);
      } else {
        setStatus(s);
      }
      setLoading(false);
    };

    // A. Subscribe FIRST (catch fast events)
    sdk.onStatusChanged(onStatusChanged).catch((err) => {
      console.error('Failed to subscribe to status changes', err);
    });

    // B. Fetch Initial Status (check current state)
    sdk
      .status()
      .then((s) => {
        if (isActive) {
          setStatus(s);
          setLoading(false);
        }
      })
      .catch((err) => {
        // If status fetch fails, we assume not connected, but stop loading
        console.error('Failed to fetch initial status', err);
        if (isActive) {
          setStatus(undefined);
          setLoading(false);
        }
      });

    // C. Cleanup
    return () => {
      isActive = false;
      sdk.removeOnStatusChanged(onStatusChanged).catch(console.error);
    };
  }, []);

  return {
    connect,
    disconnect,
    status,
    isLoading, // Exported so the Provider knows when we are ready
  };
};
